"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.inputToVideoSpec = void 0;
const deepmerge_1 = __importDefault(require("deepmerge"));
const moment_timezone_1 = __importDefault(require("moment-timezone"));
const vlx_types_1 = require("@sundaysky/vlx-types");
const creatives_1 = require("./creatives");
const logic_1 = require("./logic");
const inliner_1 = require("./inliner");
const mapping_1 = require("./mapping");
const specbuilder_1 = require("./specbuilder");
const narrations_1 = require("./narrations");
const narrations2_1 = require("./narrations2");
const analyticsDataSources_1 = require("./analyticsDataSources");
const dataContext_1 = require("./dataContext");
const logger_1 = require("@sundaysky/logger");
const log = (0, logger_1.logger)("run");
const resolveDerivedData = (definition, context) => {
    let resolved = (0, logic_1.resolveTree)(definition, (0, dataContext_1.wrapDataContext)(context));
    // merge to context
    let merged = (0, deepmerge_1.default)(context, resolved);
    // merge metadata symbol, which is skipped by deepmerge module :(
    merged[vlx_types_1.METADATA] = (0, deepmerge_1.default)(context[vlx_types_1.METADATA] || {}, resolved[vlx_types_1.METADATA] || {});
    return merged;
};
const resolveVariables = (definition, context) => {
    (0, logic_1.addVariablesToContext)(definition, context, []);
};
const addSystemDataElements = (dataContext) => {
    const selectedTimeZone = dataContext["TimeZone"] || "America/New_York";
    dataContext["Now"] = dataContext["Now"] || moment_timezone_1.default.tz(selectedTimeZone).valueOf();
    return dataContext;
};
const inputToVideoSpec = (body, skipNarration, strict, eventLogger, dtaasEndpoint, passthoughHeaders) => __awaiter(void 0, void 0, void 0, function* () {
    let cntx = {};
    const inlineconfig = {
        strict: strict
    };
    const programInlineConfig = Object.assign({}, inlineconfig, { strict: false });
    const creativeInlineConfig = Object.assign({}, inlineconfig, { strict: false });
    const startStage = (stage, data) => eventLogger === null || eventLogger === void 0 ? void 0 : eventLogger.startStage(stage, data);
    const endStage = (stage, data) => eventLogger === null || eventLogger === void 0 ? void 0 : eventLogger.endStage(stage, data);
    startStage("inlining-repositories");
    cntx.repos = yield (0, inliner_1.inline)(body.repositories, null, null, inlineconfig, passthoughHeaders, eventLogger);
    endStage("inlining-repositories");
    startStage("inlining-program");
    const program = body.program ? body.program : { hsrc: "program", repository: "program" };
    cntx.program = yield (0, inliner_1.inline)(program, null, cntx.repos, programInlineConfig, passthoughHeaders, eventLogger);
    endStage("inlining-program");
    // feed data
    cntx.dataContext = (0, dataContext_1.unwrapDataContext)(body.data) || {};
    // built in data
    cntx.dataContext = addSystemDataElements(cntx.dataContext);
    startStage("story-name-resolve");
    // the passed context may not have already been wrapped in a 'dataelements' section
    // resolve any logic in program
    cntx.program = (0, logic_1.resolveTree)(cntx.program, (0, dataContext_1.wrapDataContext)(cntx.dataContext));
    const storyName = cntx.program.storyName;
    cntx.story = Object.assign({}, body.story);
    if (storyName && typeof storyName === "string" && (cntx.story && !cntx.story.hsrc)) {
        //we will inject story name only if we didn't get it in the request body
        cntx.story.hsrc = storyName;
        cntx.story.repository = "story";
    }
    endStage("story-name-resolve");
    startStage("inlining-story");
    cntx.story = yield (0, inliner_1.inline)(cntx.story, null, cntx.repos, inlineconfig, passthoughHeaders, eventLogger);
    endStage("inlining-story");
    startStage("creative");
    let creative;
    if (cntx.repos.creative && cntx.story.creativeSettings) {
        const { storyId, creativeDDE } = cntx.story.creativeSettings || {};
        log.info(`Story contains creative settings with storyId: ${storyId} and creative DDE: ${creativeDDE}`);
        const creativeDDEValue = creativeDDE && cntx.dataContext[creativeDDE];
        if (creativeDDEValue) {
            const formattedDDEValue = typeof creativeDDEValue === "string" ? creativeDDEValue.toLowerCase() : creativeDDEValue;
            const hsrc = storyId ? `${formattedDDEValue}/${storyId}` : `${formattedDDEValue}`;
            const creativeRoot = {
                hsrc: hsrc,
                repository: "creative"
            };
            creative = yield (0, inliner_1.inline)(creativeRoot, null, cntx.repos, creativeInlineConfig, passthoughHeaders, eventLogger);
        }
    }
    if (creative === null || creative === void 0 ? void 0 : creative.creativeData) {
        log.info("Found creative data");
        const creativeData = (0, creatives_1.parseCreativeData)(creative.creativeData);
        // merge into dataContext
        cntx.dataContext = Object.assign(cntx.dataContext, creativeData);
    }
    endStage("creative");
    startStage("mappingtables");
    // Resolve story data element before mapping tables, so it can be used in mapping tables https://sundaysky.atlassian.net/browse/HB-5564
    if (cntx.story.variables) {
        let storyVariable = cntx.story.variables.find(variable => variable.name.toLowerCase() === "story");
        if (storyVariable) {
            resolveVariables([storyVariable], cntx.dataContext);
        }
    }
    cntx.dataContext = yield (0, mapping_1.resolveMappingTables)(cntx.repos, cntx.story, cntx.dataContext, dtaasEndpoint, passthoughHeaders);
    endStage("mappingtables");
    startStage("deriveddata");
    if (body.derivedData) {
        cntx.dataContext = resolveDerivedData(body.derivedData, cntx.dataContext);
    }
    if (body.variables) {
        resolveVariables(body.variables, cntx.dataContext);
    }
    if (cntx.story.derivedData) {
        cntx.dataContext = resolveDerivedData(cntx.story.derivedData, cntx.dataContext);
    }
    if (cntx.story.variables) {
        resolveVariables(cntx.story.variables, cntx.dataContext);
    }
    endStage("deriveddata");
    startStage("absolutify");
    cntx.dataContext = yield (0, inliner_1.inline)(cntx.dataContext, null, cntx.repos, inlineconfig, passthoughHeaders, eventLogger, true);
    endStage("absolutify");
    startStage("buildspec");
    cntx.spec = yield (0, specbuilder_1.buildVideoSpec)(cntx.story, (0, dataContext_1.wrapDataContext)(cntx.dataContext));
    endStage("buildspec");
    // patch - copy requestid from header to body, since header may not yet be supported
    const rid = (passthoughHeaders && passthoughHeaders["x-ssky-rid"]);
    if (rid) {
        cntx.spec["requestid"] = rid;
    }
    startStage("narration", { skip: skipNarration });
    if (!skipNarration) {
        if (cntx.story.narrationsVersion === 2) {
            (0, narrations2_1.resolveNarrationPartsV2)(cntx.spec.lineup, cntx.story.narrator, cntx.repos, cntx.story.pronunciationDefinitions);
        }
        else {
            yield (0, narrations_1.resolveNarrationParts)(cntx.spec.lineup, cntx.story.narrator, cntx.repos, cntx.dataContext, passthoughHeaders, dtaasEndpoint);
        }
    }
    endStage("narration");
    //add analyticsDataSources section to videoSpec
    //important this stage should stay last because it relies on the resolved videospec
    if (cntx.story.analyticsDataSources) {
        startStage("addAnalyticsDataSourcesToSpec");
        cntx.spec.analyticsDataSources = (0, analyticsDataSources_1.calculateAnalyticsDataSources)(cntx.story.analyticsDataSources, cntx.spec, (0, dataContext_1.wrapDataContext)(cntx.dataContext));
        endStage("addAnalyticsDataSourcesToSpec");
    }
    return cntx;
});
exports.inputToVideoSpec = inputToVideoSpec;
//# sourceMappingURL=run.js.map